import React from "react";
import {
    FinancePartnerDetailsResponse,
    UtilityDetailsResponse,
} from "../../../../../services/oneButtonServices/opportunityValidationService";
import {Flex, Table} from "antd";
import {USD, renderPairItem, renderStatusIcon, areStringsEqual} from "./OpportunityValidationShared";
import {ValidationStatusSwitch} from "./ValidationStatusSwitch";
import {FaRobot} from "react-icons/fa";

interface UtilityInfoProps {
    opportunityId: string;
    utility: UtilityDetailsResponse;
    financePartner: FinancePartnerDetailsResponse;
}

export const UtilityInfo: React.FC<UtilityInfoProps> = ({opportunityId, utility, financePartner}) => {

    const oppId = Number(opportunityId);

    const columns = [
        {
            title: 'SFDC',
            dataIndex: 'items',
            width: '32%',
            render: (value: any) => renderPairItem(value[0]),
        },
        {
            title: financePartner.financePartnerType,
            dataIndex: 'items',
            width: '20%',
            render: (value: any) => renderPairItem(value[1]),
        },
        {
            title: 'E-Bill',
            dataIndex: 'items',
            width: '32%',
            render: (value: any) => value[2]?.score
                ? renderPairItemWithScore(value[2])
                : renderPairItem(value[2]),
        },
        {
            title: 'Validate',
            dataIndex: 'validate',
            width: '8%',
            align: 'center',
            render: (value: any) => value
                ? <ValidationStatusSwitch
                    oppId={oppId}
                    source={'ebill'}
                    field={value.field}
                    status={value.status}/>
                : undefined,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: '8%',
            align: 'center',
        },
    ];

    const items = [
        {
            items: [
                {
                    name: 'Utility Company',
                    value: utility.sfdc.utilityCompany,
                },
                {
                    name: 'Current Plan (Utility Name)',
                    value: financePartner.financepartnerCurrentPlan,
                },
                {
                    name: 'Utility Name',
                    value: utility.eBill.utilityName,
                    score: utility.score.utilityNameScore,
                },
            ],
            validate: {
                field: 'utilityName',
                status: utility.status.utilityNameScoreStatus,
            },
            status: renderStatusIcon(areStringsEqual(
                utility.sfdc.utilityCompany,
                financePartner.financepartnerCurrentPlan,
                utility.eBill.utilityName)),
        },
        {
            items: [
                {
                    name: 'Account Number',
                    value: utility.sfdc.accountNumber,
                },
                null,
                {
                    name: 'Account Number',
                    value: utility.eBill.eBillAccountNumber,
                    score: utility.score.accountNumberScore,
                },
            ],
            validate: {
                field: 'eBillAccountNumber',
                status: utility.status.accountNumberScoreStatus,
            },
            status: renderStatusIcon(areStringsEqual(
                utility.sfdc.accountNumber,
                utility.eBill.eBillAccountNumber)),
        },
        {
            items: [
                {
                    name: 'Meter Number',
                    value: utility.sfdc.meterNumber,
                },
                null,
                {
                    name: 'Meter Number',
                    value: utility.eBill.eBillMeterNumber,
                    score: utility.score.meterNumberScore,
                },
            ],
            validate: {
                field: 'eBillMeterNumber',
                status: utility.status.meterNumberScoreStatus,
            },
            status: renderStatusIcon(areStringsEqual(
                utility.sfdc.meterNumber,
                utility.eBill.eBillMeterNumber)),
        },
        {
            items: [
                null,
                null,
                {
                    name: 'Name',
                    value: utility.eBill.name,
                    score: utility.score.nameScore,
                },
            ],
            validate: {
                field: 'name',
                status: utility.status.nameScoreStatus,
            },
            status: renderStatusIcon(utility.eBill.name != null),
        },
        {
            items: [
                null,
                null,
                {
                    name: 'Date',
                    value: utility.eBill.date,
                    score: utility.score.dateScore,
                },
            ],
            validate: {
                field: 'date',
                status: utility.status.dateScoreStatus,
            },
            status: renderStatusIcon(utility.eBill.date != null),
        },
        {
            items: [
                {
                    name: '12 mos usage (kwh)',
                    value: utility.sfdc.utilityUsage,
                },
                null,
                {
                    name: 'Annual Usage',
                    value: utility.eBill.annualUsage,
                    score: utility.score.annualUsageScore,
                },
            ],
            validate: {
                field: 'annualUsage',
                status: utility.status.annualUsageScoreStatus,
            },
            status: renderStatusIcon(
                utility.sfdc.utilityUsage != null &&
                utility.eBill.annualUsage != null &&
                Number(utility.sfdc.utilityUsage) === Number(utility.eBill.annualUsage)),
        },
        {
            items: [
                {
                    name: 'LID',
                    value: utility.sfdc.lid,
                },
                null,
                {
                    name: 'LID',
                    value: utility.eBill.eBillLid,
                    score: utility.score.lidScore,
                },
            ],
            status: renderStatusIcon(
                utility.sfdc.lid === 'true' &&
                utility.eBill.eBillLid != null),
        },
        {
            items: [
                null,
                {
                    name: 'Estimated Annual Utility Increase (Escalator)',
                    value: financePartner.financepartnerEstAnnualUtilityIncrease,
                },
                null,
            ],
        },
        {
            items: [
                {
                    name: 'PPA Rate',
                    value: utility.sfdc.ppaRate != null ? USD.format(utility.sfdc.ppaRate) : '',
                },
                null,
                null,
            ],
            status: renderStatusIcon(checkPpa(utility.sfdc.ppaRate)),
        },
        {
            items: [
                {
                    name: 'PPA Rate Escalator',
                    value: utility.sfdc.ppaRateEscalator,
                },
                null,
                null,
            ],
            status: renderStatusIcon(checkPpa(utility.sfdc.ppaRateEscalator)),
        },
    ];

    const renderPairItemWithScore = (value: any) => {
        return (
            <Flex>
                <div style={{width: '40%'}}><b>{value.name}</b></div>
                <div style={{width: '55%'}}>{value.value}</div>
                <div style={{width: '5%'}}>
                    <FaRobot
                        fontSize={'18px'}
                        title={`Prediction Score: ${value.score}`}
                        color={resolveScoreColor(value.score)}/>
                </div>
            </Flex>
        );
    };

    const resolveScoreColor = (score: number) => {
        if (score >= 70) {
            return 'green';
        }

        if (score >= 40) {
            return 'yellow';
        }

        return 'red';
    }

    return (
        <Table
            size={'small'}
            pagination={false}
            // @ts-ignore
            columns={columns}
            dataSource={items}/>
    );
};

function checkPpa(ppa: number | string | null) {
    if (!ppa) {
        return false;
    }

    const value = parseFloat(String(ppa).replace(/[$%]/g, '').trim()) || 0;

    return value > 0;
}